
import axios from 'axios';

// Create axios instance
const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Auth Helper
export const setAuthToken = (token) => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('token', token);
    } else {
        delete api.defaults.headers.common['Authorization'];
        localStorage.removeItem('token');
    }
};

// Check for existing token
const token = localStorage.getItem('token');
if (token) {
    setAuthToken(token);
}

// Interceptor for 401
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Clear token
            setAuthToken(null);
            // Let the app handle the state change without reload
        }
        return Promise.reject(error);
    }
);

export const auth = {
    login: async (email, password) => {
        try {
            console.log("Logging in via POST JSON...", { email });
            // Use POST JSON - most standard method
            const res = await api.post('/auth/login', { email, password });
            return res.data;
        } catch (err) {
            console.error("Login API Error:", err);
            throw err;
        }
    },
    me: async () => {
        const res = await api.get('/auth/me');
        return res.data;
    },
    register: async (userData) => {
        return api.post('/auth/register', userData);
    },
    requestPasswordReset: async (email) => {
        const res = await api.post('/auth/request-reset', { email });
        return res.data;
    },
    verifyPasswordReset: async (email, token, newPassword) => {
        const res = await api.post('/auth/verify-reset', { 
            email, 
            token, 
            new_password: newPassword 
        });
        return res.data;
    },
    resetPassword: async (email, newPassword) => {
        const res = await api.post('/auth/reset-password', { email, new_password: newPassword });
        return res.data;
    }
};

export const db = {
    // Projects
    getProjects: () => api.get('/projects').then(r => r.data),
    createProject: (data) => api.post('/projects', data).then(r => ({ id: r.data.id, ...r.data })),
    updateProject: (id, data) => api.put(`/projects/${id}`, data).then(r => r.data),
    deleteProject: (id) => api.delete(`/projects/${id}`),

    // Inventory
    getInventory: () => api.get('/inventory').then(r => r.data),
    createItem: (data) => api.post('/inventory', data).then(r => ({ id: r.data.id, ...r.data })),
    updateItem: (id, data) => api.put(`/inventory/${id}`, data).then(r => r.data),
    deleteItem: (id) => api.delete(`/inventory/${id}`),

    // Transactions
    getTransactions: () => api.get('/transactions').then(r => r.data),
    createTransaction: (data) => api.post('/transactions', data).then(r => ({ id: r.data.id, ...r.data })),
    getTransactionLogs: (params) => api.get('/transaction-logs', { params }).then(r => r.data),
    getTransactionDetail: (id) => api.get(`/transaction-logs/${id}`).then(r => r.data),

    // Suppliers
    getSuppliers: () => api.get('/suppliers').then(r => r.data),
    createSupplier: (data) => api.post('/suppliers', data).then(r => ({ id: r.data.id, ...r.data })),
    updateSupplier: (id, data) => api.put(`/suppliers/${id}`, data).then(r => r.data),
    deleteSupplier: (id) => api.delete(`/suppliers/${id}`),

    // POs
    getPOs: () => api.get('/purchase_orders').then(r => r.data),
    createPO: (data) => api.post('/purchase_orders', data).then(r => ({ id: r.data.id, ...r.data })),
    updatePO: (id, data) => api.put(`/purchase_orders/${id}`, data).then(r => r.data),
    deletePO: (id) => api.delete(`/purchase_orders/${id}`),

    // Stock Checks
    getStockChecks: () => api.get('/stock_checks').then(r => r.data),
    createStockCheck: (data) => api.post('/stock_checks', data).then(r => ({ id: r.data.id, ...r.data })),
    updateStockCheck: (id, data) => api.put(`/stock_checks/${id}`, data).then(r => r.data),

    // Users
    getUsers: () => api.get('/users').then(r => r.data),
    createUser: (data) => api.post('/users', data).then(r => ({ id: r.data.id, ...r.data })),
    updateUser: (id, data) => api.put(`/users/${id}`, data).then(r => r.data),
    deleteUser: (id) => api.delete(`/users/${id}`),
    
    // AI Insights
    generateInsights: (projectId) => api.post('/ai/generate-insights', { projectId }).then(r => r.data),
    generateForecast: (projectId) => api.post('/ai/generate-forecast', { projectId }).then(r => r.data),
};

export default api;
